import { getLocationIdx } from "@/utils/wLocation";
import { Select, Input, Modal, TableProps } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { OrderItemLocation, OrderItemV2, SpecialHandlingConfirm, SpecialHandlingStateEnum, WLocation } from "@/types/inventoryV2";
import useInspactionEnum from "@/pages/Warehouse/pages/Inspection/hooks/useInspactionEnum";
import { Product } from "@/types/product";
import CommonTable from "@/components/CommonTable";

export interface OrderConfirmInspectionRow extends SpecialHandlingConfirm {
  location: WLocation;
  product: Product;
}

interface OrderConfirmInspectionProps {
  items: OrderItemV2[];
  open: boolean;
  onOk: (data: SpecialHandlingConfirm[]) => void;
  onClose: () => void;
}

const OrderConfirmInspection: FC<OrderConfirmInspectionProps> = ({ items, open, onOk, onClose }) => {
  const { t } = useTranslation();
  const { SpecialHandlingStateList, getSpecialHandling } = useInspactionEnum();
  const [rows, setRows] = useState<OrderConfirmInspectionRow[]>([]);

  useEffect(() => {
    if (!open) return;
    let locItems: OrderItemLocation[] = [];
    items.forEach(i => {
      i.locations.forEach(il => {
        locItems.push({
          ...il,
          orderItem: { ...i, locations: [] }
        })
      })
    });
    setRows(locItems.map(locItem => ({
      location: locItem.location,
      locationId: locItem.id,
      product: locItem.orderItem.product,
      state: null,
      remark: ''
    })))
  }, [open, items]);

  const handleOk = () => {
    onOk(rows.map(({ locationId, state, remark }) => ({ locationId, state, remark })));
  };

  const handleChange = (id: string, patch: Partial<SpecialHandlingConfirm>) => {
    setRows(last => last.map(row => row.locationId === id ? { ...row, ...patch } : row));
  };

  const columns: TableProps<OrderConfirmInspectionRow>['columns'] = [
    {
      title: t('product.code'),
      key: "productCode",
      width: 120,
      render: (_, record) => record.product?.code || '',
    },
    {
      title: t('product.name'),
      key: "productName",
      width: 150,
      render: (_, record) => record.product?.name || '',
    },
    {
      title: t('counting.locationIdx'),
      key: "locationIdx",
      width: 200,
      render: (_, record) => getLocationIdx(record.location),
    },
    {
      title: t('inspection.specialHandlingState'),
      key: "state",
      width: 150,
      render: (_, record) => (
        <Select
          style={{ width: "100%" }}
          value={record.state}
          onChange={val => handleChange(record.locationId, { state: val })}
          options={[...SpecialHandlingStateList, getSpecialHandling(null)!]}
        />
      ),
    },
    {
      title: t('inspection.specialHandlingRemark'),
      key: "specialHandlingRemark",
      width: 150,
      render: (_, record) => (
        <Input
          value={record.remark ?? ''}
          onChange={e => handleChange(record.locationId, { remark: e.target.value })}
          placeholder={t('inspection.specialHandlingRemark')}
        />
      ),
    },
  ];

  return (
    <Modal
      title={t('inspection.specialHandlingAggregate')}
      open={open}
      width={700}
      onCancel={onClose}
      onOk={handleOk}
    >
      <CommonTable<OrderConfirmInspectionRow>
        rowKey="locationId"
        size="small"
        columns={columns as Array<AnyObject>}
        dataSource={rows}
        virtual
        pagination={false}
        bordered={false}
        scroll={{ x: 700, y: 400 }}
      />
    </Modal>
  )
};

export default OrderConfirmInspection;
